import { Link, useLocation } from "react-router-dom";
import { Phone, Menu, X } from "lucide-react";
import { useEffect, useState } from "react";
import { UsFlag } from "./UsFlag";
import LogoMark from "../assets/logo-mark.png";

const LOGO_URL = "/brand/bespoke-logo.png";
const PHONE_DISPLAY = "(833) 8BESPOKE";
const PHONE_TEL = "+18338237765";
const EMAIL = "hello@bespokeexteriorsolutions.com";

const nav = [
  { to: "/", label: "Home" },
  { to: "/founding-partner", label: "Founding 100" },
  { to: "/why", label: "Why Us" },
  { to: "/services", label: "Services" },
  { to: "/industries", label: "Industries" },
  { to: "/gallery", label: "Gallery" },
  { to: "/pricing", label: "Plans" },
  { to: "/one-off", label: "À La Carte" },
  { to: "/learn", label: "Learn" },
  { to: "/contact", label: "Contact" },
];

export default function Layout({ children }) {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const closeMenu = () => setMenuOpen(false);

  useEffect(() => {
    function onScroll() { setScrolled(window.scrollY > 40); }
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen relative" style={{ zIndex: 2, color: "var(--text)" }}>
      {/* Vertical brand rail */}
      <div className="brand-rail" aria-hidden>Bespoke · Est. 2013 · Veteran Owned</div>

      <header
        className="fixed top-0 left-0 right-0 z-40 transition-all duration-500"
        data-testid="site-header"
        style={{
          background: scrolled ? "rgba(6,6,6,0.72)" : "rgba(6,6,6,0.35)",
          backdropFilter: scrolled ? "blur(24px) saturate(140%)" : "blur(14px) saturate(120%)",
          WebkitBackdropFilter: scrolled ? "blur(24px) saturate(140%)" : "blur(14px) saturate(120%)",
          borderBottom: `1px solid ${scrolled ? "var(--line)" : "transparent"}`,
        }}
        data-cursor="hover"
      >
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-14 h-24 md:h-28 flex items-center justify-between gap-6">
          <Link to="/" className="flex items-center gap-3 min-w-0" data-testid="brand-link" onClick={closeMenu}>
            <span className="hidden sm:flex items-center" style={{ opacity: 0.85 }} data-testid="brand-flag-left">
              <UsFlag size={22} />
            </span>
            <div className="logo-medallion" style={{ width: 76, height: 76 }}>
              <img
                src={LogoMark}
                alt="Bespoke Exterior Solutions"
                className="logo-mono object-contain shrink-0"
                style={{ width: "90%", height: "90%" }}
              />
            </div>
            <span className="hidden sm:flex items-center" style={{ opacity: 0.85 }} data-testid="brand-flag-right">
              <UsFlag size={22} />
            </span>
            <div className="hidden md:block leading-tight ml-2">
              <div className="font-display text-2xl" style={{ letterSpacing: "-0.02em", color: "var(--text)" }}>Bespoke</div>
              <div className="font-mono text-[9px] tracking-[0.44em] uppercase mt-1.5" style={{ color: "var(--gold)" }}>Exterior · Solutions</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-10">
            {nav.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className="font-mono text-[10px] uppercase tracking-[0.32em] transition-colors relative group"
                style={{ color: location.pathname === n.to ? "var(--gold)" : "var(--text-2)" }}
              >
                {n.label}
                <span
                  className="absolute -bottom-1 left-0 h-px transition-all duration-500"
                  style={{
                    width: location.pathname === n.to ? "100%" : "0%",
                    background: "var(--gold)",
                  }}
                />
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <a
              href={`tel:${PHONE_TEL}`}
              data-testid="header-call-btn"
              className="hidden sm:inline-flex items-center gap-2.5 font-mono text-[10px] uppercase tracking-[0.32em] px-4 py-3 transition-all duration-500"
              style={{ borderTop: "1px solid var(--line-gold)", borderBottom: "1px solid var(--line-gold)", color: "var(--gold)" }}
            >
              <Phone size={12} /> {PHONE_DISPLAY}
            </a>
            <a href={`tel:${PHONE_TEL}`} data-testid="mobile-call-btn" className="sm:hidden inline-flex items-center justify-center h-11 w-11 border transition-colors" style={{ borderColor: "var(--line-gold)", color: "var(--gold)" }} aria-label="Call">
              <Phone size={16} />
            </a>
            <button onClick={() => setMenuOpen((v) => !v)} data-testid="mobile-menu-btn" className="lg:hidden inline-flex items-center justify-center h-11 w-11 border transition-colors" style={{ borderColor: "var(--line-2)", color: "var(--text)" }} aria-label="Menu">
              {menuOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="lg:hidden border-t" style={{ borderColor: "var(--line)", background: "rgba(6,6,6,0.92)", backdropFilter: "blur(20px)" }} data-testid="mobile-menu">
            <div className="px-6 py-6 space-y-1">
              {nav.map((n) => (
                <Link key={n.to} to={n.to} onClick={closeMenu} data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`} className="block px-3 py-4 font-mono text-xs uppercase tracking-[0.32em] border-l-2 transition-colors" style={{ borderColor: location.pathname === n.to ? "var(--gold)" : "transparent", color: location.pathname === n.to ? "var(--gold)" : "var(--text-2)" }}>
                  {n.label}
                </Link>
              ))}
              <a href={`mailto:${EMAIL}`} onClick={closeMenu} className="block px-3 py-4 font-mono text-[10px] uppercase tracking-[0.32em]" style={{ color: "var(--text-3)" }}>{EMAIL}</a>
            </div>
          </div>
        )}
      </header>

      <main className="pt-0 relative" style={{ zIndex: 2 }}>{children}</main>

      <footer className="mt-32 border-t relative overflow-hidden" style={{ borderColor: "var(--line)", background: "linear-gradient(180deg, var(--bg) 0%, #030303 100%)" }} data-testid="site-footer">
        {/* Ambient bottom glow */}
        <div className="absolute inset-x-0 bottom-0 h-64 pointer-events-none" style={{
          background: "radial-gradient(600px 300px at 50% 100%, rgba(198,168,124,0.06), transparent 70%)",
        }} />

        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-14 py-24 relative">
          <div className="grid md:grid-cols-12 gap-14">
            <div className="md:col-span-5">
              <div className="flex items-center gap-4">
                <UsFlag size={40} />
                <div className="logo-medallion" style={{ width: 168, height: 168 }}>
                  <img
                    src={LogoMark}
                    alt="Bespoke Exterior Solutions"
                    className="logo-mono object-contain"
                    style={{ width: "90%", height: "90%" }}
                  />
                </div>
                <UsFlag size={40} />
              </div>
              <p className="mt-8 max-w-md font-display" style={{ lineHeight: 1.3, fontSize: "1.75rem", color: "var(--text)" }}>
                A veteran-owned partnership for the finest commercial properties in <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>NYC</span> and <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>North Carolina</span>.
              </p>
              <div className="mt-8 pill">
                <span style={{ letterSpacing: 4 }}>◇◆◇◆◇</span>
                <span>Veteran Owned · Est. 2013</span>
              </div>
            </div>
            <div className="md:col-span-3">
              <div className="overline mb-6">Navigate</div>
              <ul className="space-y-3 font-body text-base" style={{ color: "var(--text-2)" }}>
                {nav.map((n) => (
                  <li key={n.to}>
                    <Link to={n.to} className="hover:underline underline-offset-4 transition-colors" style={{ }} onMouseEnter={(e) => e.currentTarget.style.color = "var(--gold)"} onMouseLeave={(e) => e.currentTarget.style.color = ""}>{n.label}</Link>
                  </li>
                ))}
                <li><Link to="/about" onMouseEnter={(e) => e.currentTarget.style.color = "var(--gold)"} onMouseLeave={(e) => e.currentTarget.style.color = ""} className="hover:underline underline-offset-4">About</Link></li>
                <li><Link to="/process" onMouseEnter={(e) => e.currentTarget.style.color = "var(--gold)"} onMouseLeave={(e) => e.currentTarget.style.color = ""} className="hover:underline underline-offset-4">Our Process</Link></li>
                <li><Link to="/di-water" onMouseEnter={(e) => e.currentTarget.style.color = "var(--gold)"} onMouseLeave={(e) => e.currentTarget.style.color = ""} className="hover:underline underline-offset-4">DI Water Tech</Link></li>
                <li><Link to="/referral" onMouseEnter={(e) => e.currentTarget.style.color = "var(--gold)"} onMouseLeave={(e) => e.currentTarget.style.color = ""} className="hover:underline underline-offset-4">Referral Program</Link></li>
                <li><Link to="/reel" onMouseEnter={(e) => e.currentTarget.style.color = "var(--gold)"} onMouseLeave={(e) => e.currentTarget.style.color = ""} className="hover:underline underline-offset-4">Reel 001</Link></li>
                <li><Link to="/audit" onMouseEnter={(e) => e.currentTarget.style.color = "var(--gold)"} onMouseLeave={(e) => e.currentTarget.style.color = ""} className="hover:underline underline-offset-4">AI Audit</Link></li>
                <li><Link to="/admin" className="text-sm" style={{ color: "var(--text-3)" }}>Operator Login</Link></li>
              </ul>
            </div>
            <div className="md:col-span-2">
              <div className="overline mb-6">Contact</div>
              <ul className="space-y-3 font-body text-base" style={{ color: "var(--text-2)" }}>
                <li><a href={`tel:${PHONE_TEL}`} className="hover:underline underline-offset-4">{PHONE_DISPLAY}</a></li>
                <li><a href={`mailto:${EMAIL}`} className="hover:underline underline-offset-4 break-words text-sm">{EMAIL}</a></li>
                <li style={{ color: "var(--text-3)" }}>24/7 Storm Response</li>
              </ul>
            </div>
            <div className="md:col-span-2">
              <div className="overline mb-6">Territory</div>
              <ul className="space-y-3 font-body text-base" style={{ color: "var(--text-2)" }}>
                <li>New York City · 5 Boroughs</li>
                <li>Long Island · Nassau + Suffolk</li>
                <li>Charlotte · NC</li>
                <li>Raleigh–Durham · NC</li>
                <li>Asheville · NC</li>
              </ul>
            </div>
          </div>
          <div className="hairline mt-20" />
          <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 font-mono text-[10px] uppercase tracking-[0.36em]" style={{ color: "var(--text-3)" }}>
            <div>© {new Date().getFullYear()} · Bespoke Exterior Solutions</div>
            <div style={{ color: "var(--gold)" }}>Issue No. 001 · Winter 2026</div>
          </div>
        </div>

        {/* Giant brand watermark background */}
        <div
          aria-hidden
          className="absolute -bottom-32 -right-20 font-display pointer-events-none select-none"
          style={{
            fontSize: "clamp(12rem, 26vw, 28rem)",
            color: "rgba(255,255,255,0.02)",
            letterSpacing: "-0.06em",
            lineHeight: 0.7,
          }}
        >
          Bespoke.
        </div>
      </footer>
    </div>
  );
}
